import React from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Chip,
  Box,
  Stack,
} from '@mui/material';
import {
  Quiz as QuizIcon,
  Timer as TimerIcon,
  EmojiEvents as TrophyIcon,
  PlayArrow as PlayIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { QuizSummary, QuizAttempt } from '../../types/quiz';

interface QuizCardProps {
  quiz: QuizSummary;
  attempt?: QuizAttempt | null;
  onStart?: () => void;
  onViewResults?: () => void;
}

const QuizCard: React.FC<QuizCardProps> = ({ quiz, attempt, onStart, onViewResults }) => {
  const hasAttempted = !!attempt;
  const isCompleted = attempt?.completedAt !== undefined && attempt?.completedAt !== null;

  // Calculate estimated time in minutes
  const estimatedMinutes = quiz.estimatedTimeSeconds
    ? Math.ceil(quiz.estimatedTimeSeconds / 60)
    : null;

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 4,
        },
      }}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography variant="h5" component="h2" gutterBottom fontWeight="bold">
          {quiz.title}
        </Typography>

        {quiz.description && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 2,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              minHeight: '40px',
            }}
          >
            {quiz.description}
          </Typography>
        )}

        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
          <Chip
            icon={<QuizIcon />}
            label={`${quiz.questionCount} Questions`}
            size="small"
            color="primary"
            variant="outlined"
          />
          {estimatedMinutes !== null && estimatedMinutes > 0 && (
            <Chip
              icon={<TimerIcon />}
              label={`~${estimatedMinutes} min`}
              size="small"
              color="secondary"
              variant="outlined"
            />
          )}
        </Stack>

        {hasAttempted && isCompleted && (
          <Box sx={{ mt: 2, p: 1.5, bgcolor: 'success.light', borderRadius: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <TrophyIcon sx={{ color: 'success.dark' }} />
              <Box>
                <Typography variant="body2" fontWeight="bold" color="success.dark">
                  Your Score: {attempt.score} points
                </Typography>
                <Typography variant="caption" color="success.dark">
                  Completed
                </Typography>
              </Box>
            </Stack>
          </Box>
        )}
      </CardContent>

      <CardActions sx={{ p: 2, pt: 0 }}>
        {!hasAttempted ? (
          <Button
            fullWidth
            variant="contained"
            color="primary"
            size="large"
            startIcon={<PlayIcon />}
            onClick={onStart}
          >
            Start Quiz
          </Button>
        ) : isCompleted ? (
          <Button
            fullWidth
            variant="outlined"
            color="primary"
            size="large"
            startIcon={<ViewIcon />}
            onClick={onViewResults}
          >
            View Results
          </Button>
        ) : (
          <Button
            fullWidth
            variant="contained"
            color="warning"
            size="large"
            startIcon={<PlayIcon />}
            onClick={onStart}
          >
            Continue Quiz
          </Button>
        )}
      </CardActions>
    </Card>
  );
};

export default QuizCard;
